import { useCallback, useEffect, useState } from 'react';
import { sortDays, toggleDayIn } from './filters';
import { EMPTY_FILTERS, type Filters } from './types';

export { applyFilters, sortDays } from './filters';

/**
 * URL pre-filtering is the whole point of this page.
 *
 * Every filter reads from and writes to the query string, so a link like
 *   /?location=ontario&day=tuesday,thursday&audience=women&kids=true
 * lands pre-filtered with zero clicks — that is what gets pasted into
 * Clearstream texts and emails. Updates use replaceState so the view stays
 * copy-pasteable without burying the user's original page under history
 * entries they never asked for.
 */

function readFromLocation(): Filters {
  const params = new URLSearchParams(window.location.search);
  const get = (key: string) => {
    const value = params.get(key)?.trim().toLowerCase();
    return value ? value : null;
  };
  // Accepts both `day=tuesday,thursday` (what we write — compact enough for a
  // text message) and repeated `day=tuesday&day=thursday`.
  const days = sortDays(
    params
      .getAll('day')
      .flatMap((value) => value.split(','))
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean)
  );
  // `childcare` is the legacy param name, still read so links already sent
  // keep working. New links write `kids`.
  const kids = get('kids') ?? get('childcare');
  return {
    // Not lower-cased through `get`: this one is echoed back into the search
    // box and the empty state, so it keeps whatever the person actually typed.
    query: params.get('q')?.trim() ?? '',
    // `campus` is the legacy name for this filter, still read so links already
    // sent out keep working. New links get `location`, which is accurate now
    // that the option list includes Homes alongside the campuses.
    location: get('location') ?? get('campus'),
    days,
    audience: get('audience'),
    kidsMinistry: kids === 'true' || kids === '1' || kids === 'yes',
  };
}

function writeToLocation(filters: Filters) {
  const params = new URLSearchParams(window.location.search);

  const set = (key: string, value: string | null) => {
    if (value) params.set(key, value);
    else params.delete(key);
  };

  set('q', filters.query.trim() || null);
  set('location', filters.location);
  params.delete('campus'); // superseded; never write both
  params.delete('day');
  if (filters.days.length > 0) params.set('day', sortDays(filters.days).join(','));
  set('audience', filters.audience);
  set('kids', filters.kidsMinistry ? 'true' : null);
  params.delete('childcare'); // superseded; never write both

  // URLSearchParams percent-encodes commas, turning a shareable
  // `?day=tuesday,thursday` into `?day=tuesday%2Cthursday`. A comma is legal
  // unencoded in a query value, and these links get pasted into texts, so put
  // it back.
  const query = params.toString().replace(/%2C/g, ',');
  const next = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  window.history.replaceState(null, '', next);
}

export function useFilters() {
  const [filters, setFiltersState] = useState<Filters>(() =>
    typeof window === 'undefined' ? EMPTY_FILTERS : readFromLocation()
  );

  // Keep the URL authoritative: back/forward and manual edits re-drive state.
  useEffect(() => {
    const onPopState = () => setFiltersState(readFromLocation());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const setFilters = useCallback((next: Filters | ((prev: Filters) => Filters)) => {
    setFiltersState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      writeToLocation(resolved);
      return resolved;
    });
  }, []);

  /** Selecting the already-selected value clears it — the pill acts as a toggle. */
  const toggle = useCallback(
    (key: 'location' | 'audience', value: string | null) => {
      setFilters((prev) => ({ ...prev, [key]: prev[key] === value ? null : value }));
    },
    [setFilters]
  );

  /**
   * Day is multi-select: passing a day adds or removes it, passing null
   * ("All") clears the whole set.
   */
  const toggleDay = useCallback(
    (value: string | null) => {
      setFilters((prev) => ({ ...prev, days: toggleDayIn(prev.days, value) }));
    },
    [setFilters]
  );

  const setKidsMinistry = useCallback(
    (value: boolean) => setFilters((prev) => ({ ...prev, kidsMinistry: value })),
    [setFilters]
  );

  const setQuery = useCallback(
    (value: string) => setFilters((prev) => ({ ...prev, query: value })),
    [setFilters]
  );

  const clear = useCallback(() => setFilters(EMPTY_FILTERS), [setFilters]);

  // Counts filter categories in use, not individual days — the badge answers
  // "how many things are narrowing this list", and three days is still one
  // narrowing. The search box counts too: it narrows like the rest, and
  // "Clear filters" clears it, so leaving it out would make the badge lie.
  const activeCount =
    (filters.query.trim() ? 1 : 0) +
    (filters.location ? 1 : 0) +
    (filters.days.length > 0 ? 1 : 0) +
    (filters.audience ? 1 : 0) +
    (filters.kidsMinistry ? 1 : 0);

  return { filters, toggle, toggleDay, setKidsMinistry, setQuery, clear, activeCount };
}
